import React, { useEffect, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Collapse,
  Tooltip,
  Chip,
  CircularProgress,
  Grid,
  Skeleton
} from '@mui/material';
import {
  Add as AddIcon,
  Assignment,
  Delete as DeleteTaskIcon,
  Download,
  Edit as EditTaskIcon,
  ExpandLess,
  ExpandMore,
  UploadFile,
  Warning as WarningIcon,
  DragIndicator, 
  OpenInNew,
  Edit,
  Info,
} from '@mui/icons-material';

import { supabase } from '../../supabaseClient';
import { useTranslations } from '../../constants/translations/CaseTranslation';
import { formatDate } from '../../constants/DateUtils';
import DocumentUploadModal from '../../components/Modal/DocumentUploadModal';
import DocumentMetadataModal from '../../components/Modal/DocumentMetadataModal';
import { uploadDocument, saveDocumentMetadata } from '../../constants/CommonFunctions';

const TaskEditForm = ({ task, office, onSave, onCancel, t, isValidDate }) => {
  const [formData, setFormData] = useState({
    text: task.text,
    notes: task.notes || '',
    due_date: task.due_date || '',
    is_visible: task.is_visible !== undefined ? task.is_visible : true
  });

  const handleSubmit = () => {
    if (!formData.text.trim()) return;
    onSave(task.id, formData);
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Box sx={{ width: '100%', my: 2 }}>
      <Paper 
        elevation={0}
        sx={{ 
          p: 3, 
          backgroundColor: '#f8f9fa',
          border: `2px solid ${office?.color}`,
          borderRadius: 2
        }}
      >
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2, color: office?.color }}>
          ✏️ {t.editingTask || 'Editando Tarea'}
        </Typography>
        
        <Grid container spacing={2}>
          {/* Descripción */}
          <Grid item size={{xs:12}}>
            <TextField
              fullWidth
              label={t.taskDescription || 'Descripción'}
              value={formData.text}
              onChange={(e) => handleFieldChange('text', e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.ctrlKey) {
                  handleSubmit();
                } else if (e.key === 'Escape') {
                  onCancel();
                }
              }}
              variant="outlined"
              multiline
              rows={2}
              autoFocus
              helperText={t.pressCtrlEnter || 'Ctrl + Enter para guardar, Esc para cancelar'}
            />
          </Grid>

          {/* Fecha de vencimiento */}
          <Grid item size={{xs:12, sm:6}}>
            <TextField
              fullWidth
              type="date"
              label={t.dueDate || 'Fecha de vencimiento'}
              value={formData.due_date}
              onChange={(e) => handleFieldChange('due_date', e.target.value)}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              error={formData.due_date && !isValidDate(formData.due_date)}
              helperText={
                formData.due_date && !isValidDate(formData.due_date)
                  ? (t.invalidDateFormat || 'Formato inválido')
                  : (t.optional || 'Opcional')
              }
            />
          </Grid>

          {/* Visibilidad */}
          <Grid item size={{xs:12, sm:6}}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              height: '100%',
              p: 1.5,
              backgroundColor: 'white',
              borderRadius: 1,
              border: '1px solid #e0e0e0'
            }}>
              <Checkbox
                checked={formData.is_visible}
                onChange={(e) => handleFieldChange('is_visible', e.target.checked)}
                sx={{
                  color: office?.color,
                  '&.Mui-checked': { color: office?.color }
                }}
              />
              <Box>
                <Typography variant="body2" fontWeight="medium">
                  {t.visibleToClient || 'Visible para cliente'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formData.is_visible 
                    ? (t.clientCanSee || 'El cliente puede ver')
                    : (t.clientCannotSee || 'Oculta del cliente')
                  }
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Notas internas */}
          <Grid item size={{xs:12}}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label={t.taskNotes || 'Notas internas'}
              value={formData.notes}
              onChange={(e) => handleFieldChange('notes', e.target.value)}
              variant="outlined"
              placeholder={t.taskNotesPlaceholder || 'Ej: Enviado, esperando respuesta...'}
              helperText={t.onlyVisibleToTeam || 'Solo visible para el equipo legal'}
            />
          </Grid>

          {/* Botones */}
          <Grid item size={{xs:12}}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={onCancel}
                sx={{ minWidth: 100 }}
              >
                {t.cancel || 'Cancelar'}
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!formData.text.trim()}
                startIcon={<EditTaskIcon />}
                sx={{ 
                  backgroundColor: office?.color,
                  minWidth: 120,
                  '&:hover': { backgroundColor: office?.color, opacity: 0.9 }
                }}
              >
                {t.saveChanges || 'Guardar'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

const TasksSection = ({ 
  tasks, 
  setTasks, 
  caseRole, 
  office, 
  caseData,
  readOnly = false,
  onProgressUpdate
}) => {
  const sortedTasks = [...tasks].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  const visibleTasks = caseRole === 'client' 
  ? sortedTasks.filter(task => task.is_visible)
  : sortedTasks;
  const [newTask, setNewTask] = useState('');
  const [isAddingTask, setIsAddingTask] = useState(false);
  const [openTaskConfirmModal, setOpenTaskConfirmModal] = useState(false);
  const [taskToComplete, setTaskToComplete] = useState(null);
  const [animatingTask, setAnimatingTask] = useState(null);

  const [taskDocuments, setTaskDocuments] = useState({}); // { taskId: [documents] }
  const [uploadingForTask, setUploadingForTask] = useState(null);
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [selectedTaskForUpload, setSelectedTaskForUpload] = useState(null);

  // Formulario opcional de metadata (post-subida), para el copiloto IA
  const [metadataDoc, setMetadataDoc] = useState(null); // { id, name }
  const [savingMetadata, setSavingMetadata] = useState(false);
  const [downloading, setDownloading] = useState({});
  const [expandedTasks, setExpandedTasks] = useState({}); // { taskId: true/false }

  const [newTaskDueDate, setNewTaskDueDate] = useState('');

  const [isLoadingPermissions, setIsLoadingPermissions] = useState(true);

  const [viewingNotesTaskId, setViewingNotesTaskId] = useState(null);

  const [editingTask, setEditingTask] = useState(null);
  const [newTaskVisible, setNewTaskVisible] = useState(true);

  const [downloadingAll, setDownloadingAll] = useState(false);

  const handleDownloadAllDocuments = async () => {
    setDownloadingAll(true);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(
        `${supabase.supabaseUrl}/functions/v1/case-documents-download-google-drive?case_id=${caseData.id}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${session?.access_token || supabase.supabaseKey}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || `Error HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `caso_${caseData.title}_documentos.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
    } catch (error) {
      console.error('Error downloading all documents:', error);
      alert(error.message || t.errorDownloadingDocuments || 'Error al descargar documentos');
    } finally {
      setDownloadingAll(false);
    }
  };


  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px de movimiento antes de activar drag
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const loadTaskDocuments = async () => {
    try {
      const taskIds = tasks.map(t => t.id);
      if (taskIds.length === 0) return;

      const { data, error } = await supabase
        .from('document')
        .select(`
          *,
          uploader:created_by (
            id,
            full_name
          )
        `)
        .in('task_id', taskIds)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading task documents:', error);
        return;
      }

      // Agrupar documentos por task_id
      const grouped = (data || []).reduce((acc, doc) => {
        if (!acc[doc.task_id]) {
          acc[doc.task_id] = [];
        }
        acc[doc.task_id].push(doc);
        return acc;
      }, {});

      setTaskDocuments(grouped);
    } catch (error) {
      console.error('Error loading task documents:', error);
    }
  };

  useEffect(() => {
    if (tasks.length > 0) {
      loadTaskDocuments();
    }
  }, [tasks]);

  useEffect(() => {
    // Simular carga de permisos/datos iniciales
    const timer = setTimeout(() => {
      setIsLoadingPermissions(false);
    }, 300); // Pequeño delay para que carguen los datos

    return () => clearTimeout(timer);
  }, [caseRole, caseData?.state, caseData?.is_visible, readOnly]);

  const handleOpenUploadModal = (task) => {
    if (isCaseReadOnly()) return;
    setSelectedTaskForUpload(task);
    setOpenUploadModal(true);
  };

  const handleUploadDocument = async (file, documentName) => {
    console.log('=== INICIO UPLOAD ===');
    console.log('File:', file?.name, file?.size);
    console.log('Document Name:', documentName);
    console.log('Selected Task:', selectedTaskForUpload);
    console.log('Case ID:', caseData?.id);
    console.log('Office ID:', office?.id);
    
    if (!selectedTaskForUpload) {
      console.error('❌ No task selected');
      alert('No se ha seleccionado una tarea');
      return;
    }

    if (!file) {
      console.error('❌ No file provided');
      alert('No se ha proporcionado un archivo');
      return;
    }

    setUploadingForTask(selectedTaskForUpload.id);
    
    try {
      const user = (await supabase.auth.getUser()).data.user;
      
      console.log('📤 Calling uploadDocument function...');
      
      // Usar la función centralizada
      const result = await uploadDocument({
        file: file,
        officeId: office.id,
        caseId: caseData.id,
        taskId: selectedTaskForUpload.id, // ← IMPORTANTE: incluir taskId
        uploadedBy: user?.id
      });

      console.log('✅ Upload successful:', result);

      if (result.success) {
        // Recargar documentos de tareas
        console.log('Reloading task documents...');
        await loadTaskDocuments();

        // Cerrar modal
        setOpenUploadModal(false);
        setSelectedTaskForUpload(null);

        if (result.document?.id) {
          setMetadataDoc({
            id: result.document.id,
            name: result.document.name || result.document.filename || file.name,
          });
        }

        // Mostrar mensaje de éxito
        //alert(t.fileUploadedSuccessfully || 'Archivo subido exitosamente');
      } else {
        throw new Error('Upload no exitoso');
      }
    } catch (error) {
      console.error('❌ Error uploading document:', error);
      alert(error.message || t.errorUploadingDocument || 'Error al subir documento');
    } finally {
      setUploadingForTask(null);
      console.log('=== FIN UPLOAD ===');
    }
  };

  const handleMetadataSkip = () => setMetadataDoc(null);

  const handleMetadataSave = async (form) => {
    setSavingMetadata(true);
    try {
      await saveDocumentMetadata(metadataDoc?.id, caseData.id, form);
    } catch (err) {
      console.error('Error al guardar metadata del documento:', err);
    } finally {
      setSavingMetadata(false);
      setMetadataDoc(null);
    }
  };

  const handleDeleteDocument = async (docId, taskId) => {
    if (isCaseReadOnly()) return;
    
    //if (!confirm(t.confirmDeleteDocument || '¿Eliminar este documento?')) return;

    try {
      const { error } = await supabase
        .from('document')
        .delete()
        .eq('id', docId);

      if (error) throw error;

      // Actualizar estado local
      setTaskDocuments(prev => ({
        ...prev,
        [taskId]: (prev[taskId] || []).filter(doc => doc.id !== docId)
      }));
    } catch (error) {
      console.error('Error deleting document:', error);
      alert(t.errorDeletingDocument || 'Error al eliminar documento');
    }
  };

  const handleDownloadDocument = async (doc) => {
    setDownloading(prev => ({ ...prev, [doc.id]: true }));
    
    try {
      if (doc.google_drive_id) {
        // Lógica de descarga de Google Drive
        const result = await downloadFromGoogleDrive(doc.id, doc.filename);
        if (!result.success) throw new Error(result.error);
      } else if (doc.storage_path) {
        const { data, error } = await supabase.storage
          .from('files')
          .createSignedUrl(doc.storage_path, 60);

        if (error) throw new Error(t.errorDownloadingDocument);
        window.open(data.signedUrl, '_blank');
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      alert(error.message || t.errorDownloadingDocument);
    } finally {
      setDownloading(prev => ({ ...prev, [doc.id]: false }));
    }
  };

  const downloadFromGoogleDrive = async (documentId, filename) => {
    // Copiar la lógica de tu CaseDocumentSection
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(
        `${supabase.supabaseUrl}/functions/v1/document-download-google-drive?document_id=${documentId}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${session?.access_token || supabase.supabaseKey}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || `Error HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      return { success: true };
    } catch (error) {
      console.error('Error downloading from Google Drive:', error);
      return { success: false, error: error.message };
    }
  };

  const toggleTaskExpand = (taskId) => {
    setExpandedTasks(prev => ({
      ...prev,
      [taskId]: !prev[taskId]
    }));
  };

  // Verificar si el documento es de Word/Google Docs
  const isWordDocument = (doc) => {
    const extension = doc.extension?.toLowerCase() || '';
    const mimeType = doc.mime_type?.toLowerCase() || '';
    
    const wordExtensions = ['doc', 'docx'];
    const wordMimeTypes = ['word', 'document', 'msword', 'wordprocessingml'];
    
    return wordExtensions.includes(extension) || 
          wordMimeTypes.some(type => mimeType.includes(type));
  };

  // Verificar si es un documento de Google (Sheets, Docs, Slides)
  const isGoogleDocument = (doc) => {
    const mimeType = doc.mime_type?.toLowerCase() || '';
    return mimeType.includes('google-apps');
  };

  // Obtener la URL correcta para abrir en Google Drive
  const getGoogleDriveUrl = (doc, forceEdit = false) => {
    if (!doc.google_drive_id && !doc.drive_url) {
      return null;
    }

    const fileId = doc.google_drive_id;
    
    // Si es un documento de Word, convertirlo a Google Docs para edición
    if (isWordDocument(doc) || forceEdit) {
      return `https://docs.google.com/document/d/${fileId}/edit`;
    }
    
    // Si es un documento nativo de Google, usar el drive_url o construir URL de edición
    if (isGoogleDocument(doc)) {
      if (doc.drive_url) {
        return doc.drive_url.replace('/view', '/edit');
      }
      return `https://docs.google.com/document/d/${fileId}/edit`;
    }
    
    // Para otros tipos de archivo, usar drive_url o URL de visualización
    if (doc.drive_url) {
      return doc.drive_url;
    }
    
    return `https://drive.google.com/file/d/${fileId}/view`;
  };

  // Abrir documento en Google Drive
  const handleOpenInDrive = (doc, forceEdit = false) => {
    const driveUrl = getGoogleDriveUrl(doc, forceEdit);
    
    if (!driveUrl) {
      alert(t.documentNotInDrive || 'Este documento no está en Google Drive');
      return;
    }

    // Abrir en nueva pestaña
    window.open(driveUrl, '_blank', 'noopener,noreferrer');
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const t = useTranslations(caseData?.language);

  const isCaseReadOnly = () => {
    return readOnly || caseData?.state === 'completed' || !caseData?.is_visible;
  };

  const createConfetti = (targetElement) => {
    const colors = [office?.color || '#1976d2', '#4caf50', '#ff9800', '#e91e63', '#9c27b0', '#00bcd4'];
    
    // Crear contenedor para el confeti en toda la página
    const confettiContainer = document.createElement('div');
    Object.assign(confettiContainer.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      right: '0',
      bottom: '0',
      width: '100vw',    
      height: '100vh',   
      pointerEvents: 'none',
      zIndex: '9999',    
      overflow: 'hidden'
    });
    
    // Agregar al body en lugar del elemento específico
    document.body.appendChild(confettiContainer);
    
    // Generar más confeti para cubrir toda la pantalla
    for (let i = 0; i < 50; i++) {
      const confetti = document.createElement('div');
      
      // Estilos del confeti
      Object.assign(confetti.style, {
        position: 'absolute',
        width: Math.random() * 10 + 6 + 'px',  // Ligeramente más grandes
        height: Math.random() * 10 + 6 + 'px',
        backgroundColor: colors[Math.floor(Math.random() * colors.length)],
        left: Math.random() * 100 + '%',       // Distribuido por toda la pantalla
        top: '-10px',                          // Empezar desde arriba de la pantalla
        borderRadius: Math.random() > 0.5 ? '50%' : '0%',
        opacity: '0.9',
        pointerEvents: 'none'
      });

      confettiContainer.appendChild(confetti);

      // Animación con requestAnimationFrame
      let posY = -10;  // Empezar desde arriba
      let posX = parseFloat(confetti.style.left);
      let rotation = 0;
      let velocity = Math.random() * 2 + 1;
      const fallSpeed = Math.random() * 3 + 2;    // Velocidad de caída más variada
      const drift = (Math.random() - 0.5) * 4;    // Más movimiento lateral
      const rotationSpeed = Math.random() * 15 + 5;

      const animate = () => {
        posY += fallSpeed;
        posX += drift * 0.2;
        rotation += rotationSpeed;
        velocity *= 0.98;
        
        confetti.style.top = posY + 'px';        // Usar píxeles en lugar de porcentaje
        confetti.style.left = posX + '%';
        confetti.style.transform = `rotate(${rotation}deg) scale(${velocity})`;
        confetti.style.opacity = Math.max(0, 0.9 - (posY / window.innerHeight));

        // Continuar hasta que salga de la pantalla
        if (posY < window.innerHeight + 50 && confetti.parentNode) {
          requestAnimationFrame(animate);
        } else if (confetti.parentNode) {
          confetti.remove();
        }
      };

      // Delay escalonado para cada pieza (más rápido para más fluidez)
      setTimeout(() => requestAnimationFrame(animate), i * 30);
    }
    
    // Limpiar después de la animación
    setTimeout(() => {
      if (confettiContainer.parentNode) {
        confettiContainer.remove();
      }
    }, 4000); // Tiempo extendido para permitir que todo el confeti caiga
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = sortedTasks.findIndex(t => t.id === active.id);
    const newIndex = sortedTasks.findIndex(t => t.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const reorderedTasks = arrayMove(sortedTasks, oldIndex, newIndex).map(
      (task, index) => ({
        ...task,
        display_order: index + 1
      })
    );

    setTasks(reorderedTasks);

    try {
      const updates = reorderedTasks.map(task =>
        supabase
          .from('task')
          .update({ display_order: task.display_order })
          .eq('id', task.id)
      );

      const results = await Promise.all(updates);

      const hasError = results.some(r => r.error);
      if (hasError) {
        console.error('Error updating task order:', results);
        setTasks([...tasks]); // revertir
        alert('Error al reordenar tareas');
        return;
      }

      console.log('✅ Tasks reordered successfully');
    } catch (error) {
      console.error('Error reordering tasks:', error);
      setTasks([...tasks]); // revertir
      alert('Error al reordenar tareas');
    }
  };

  const handleAddTask = async () => {
    if (!newTask.trim() || isCaseReadOnly()) return;

    if (newTaskDueDate && !isValidDate(newTaskDueDate)) {
      alert(t.invalidDateFormat || 'Formato de fecha inválido. Use AAAA-MM-DD');
      return;
    }

    try {
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) {
        alert(t.userNotAuthenticated);
        return;
      }

      const maxOrder = tasks.length > 0 
        ? Math.max(...tasks.map(t => t.display_order || 0))
        : 0;

      const taskData = {
        text: newTask.trim(),
        case_id: caseData.id,
        completed: false,
        display_order: maxOrder + 1,
        created_by: user.id,
        due_date: newTaskDueDate || null,
        is_visible: newTaskVisible,  // ← USAR el estado
        notes: null
      };

      const { data, error } = await supabase
        .from('task')
        .insert(taskData)
        .select()
        .single();

      if (error) {
        console.error('Error adding task:', error);
        alert(t.errorAddingTask);
        return;
      }

      const updatedTasks = [...tasks, data].sort((a, b) => a.display_order - b.display_order);
      setTasks(updatedTasks);
      onProgressUpdate?.(updatedTasks);

      setNewTask('');
      setIsAddingTask(false);
      setNewTaskDueDate('');
      setNewTaskVisible(true);  // ← RESETEAR
    } catch (error) {
      console.error('Error adding task:', error);
      alert(t.errorAddingTask);
    }
  };

  const handleToggleTask = async (taskId) => {
    if (isCaseReadOnly()) return;
    try {
      const taskToUpdate = tasks.find(task => task.id === taskId);
      if (!taskToUpdate) return;

      const isCompleting = !taskToUpdate.completed;

      if (isCompleting) {
        // Si se está completando, mostramos modal
        setTaskToComplete(taskToUpdate);
        setOpenTaskConfirmModal(true);
      } else {
        // Si se está reabriendo, actualizamos directamente
        const updateData = {
          completed: false,
          finish_date: null
        };

        const { error } = await supabase
          .from('task')
          .update(updateData)
          .eq('id', taskId);

        if (error) {
          console.error('Error reopening task:', error);
          alert(t.errorUpdatingTask);
          return;
        }

        // Actualizar el estado local
        const updatedTasks = tasks.map(task =>
          task.id === taskId
            ? { ...task, completed: false, finish_date: null }
            : task
        );
        setTasks(updatedTasks);
        onProgressUpdate?.(updatedTasks);
      }
    } catch (error) {
      console.error('Error updating task:', error);
      alert(t.errorUpdatingTask);
    }
  };

  const handleConfirmCompleteTask = async () => {
    if (!taskToComplete) return;

    try {
      // Iniciar animación del checkbox
      setAnimatingTask(taskToComplete.id);

      const updateData = {
        completed: true,
        finish_date: new Date().toISOString()
      };

      const { error } = await supabase
        .from('task')
        .update(updateData)
        .eq('id', taskToComplete.id);

      if (error) {
        console.error('Error updating task:', error);
        alert(t.errorUpdatingTask);
        setAnimatingTask(null);
        return;
      }

      // Actualizar el estado local
      const updatedTasks = tasks.map(task =>
        task.id === taskToComplete.id
          ? { ...task, completed: true, finish_date: updateData.finish_date }
          : task
      );
      setTasks(updatedTasks);
      onProgressUpdate?.(updatedTasks);

      // Cerrar modal
      setOpenTaskConfirmModal(false);
      setTaskToComplete(null);

      // Lanzar confeti después de un pequeño delay
      setTimeout(() => {
        // Encontrar el elemento de la lista específico
        const taskElement = document.querySelector(`[data-task-id="${taskToComplete.id}"]`);
        if (taskElement) {
          createConfetti(taskElement);
        }
        
        // Detener animación del checkbox después de más tiempo
        setTimeout(() => {
          setAnimatingTask(null);
        }, 1000);
      }, 400);

    } catch (error) {
      console.error('Error updating task:', error);
      alert(t.errorUpdatingTask);
      setAnimatingTask(null);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (isCaseReadOnly()) return;
    
    try {
      const { error } = await supabase
        .from('task')
        .delete()
        .eq('id', taskId);

      if (error) {
        console.error('Error deleting task:', error);
        alert(t.errorDeletingTask);
        return;
      }

      // Obtener las tareas restantes y reordenar
      const remainingTasks = tasks
        .filter(task => task.id !== taskId)
        .sort((a, b) => a.display_order - b.display_order);

      // Actualizar display_order secuencialmente
      for (let i = 0; i < remainingTasks.length; i++) {
        await supabase
          .from('task')
          .update({ display_order: i + 1 })
          .eq('id', remainingTasks[i].id);
        
        remainingTasks[i].display_order = i + 1;
      }

      setTasks(remainingTasks);

      onProgressUpdate?.(remainingTasks);
    } catch (error) {
      console.error('Error deleting task:', error);
      alert(t.errorDeletingTask);
    }
  };

  const handleEditTask = (taskId) => {
    if (isCaseReadOnly()) return;
    setEditingTask({ id: taskId });
  };

  const handleSaveEditFromForm = async (taskId, formData) => {
    if (!formData.text.trim() || isCaseReadOnly()) return;

    if (formData.due_date && !isValidDate(formData.due_date)) {
      alert(t.invalidDateFormat || 'Formato de fecha inválido');
      return;
    }

    try {
      const { error } = await supabase
        .from('task')
        .update({ 
          text: formData.text.trim(), 
          due_date: formData.due_date || null,
          notes: formData.notes.trim() || null,
          is_visible: formData.is_visible
        })
        .eq('id', taskId);

      if (error) {
        console.error('Error updating task:', error);
        alert(t.errorUpdatingTask);
        return;
      }

      setTasks(tasks.map(task => 
        task.id === taskId 
          ? { 
              ...task, 
              text: formData.text.trim(),
              due_date: formData.due_date || null,
              notes: formData.notes.trim() || null,
              is_visible: formData.is_visible
            } 
          : task
      ));
      
      setEditingTask(null);
    } catch (error) {
      console.error('Error updating task:', error);
      alert(t.errorUpdatingTask);
    }
  };

  const isValidDate = (dateString) => {
    if (!dateString) return true; // null/empty es válido
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date) && dateString.match(/^\d{4}-\d{2}-\d{2}$/);
  };

  const handleCancelEdit = () => {
    setEditingTask(null);
  };

  /* const handleToggleTaskVisibility = async (taskId, currentVisibility) => {
    if (isCaseReadOnly() || caseRole === 'client') return;

    try {
      const { error } = await supabase
        .from('task')
        .update({ is_visible: !currentVisibility })
        .eq('id', taskId);

      if (error) {
        console.error('Error updating task visibility:', error);
        alert(t.errorUpdatingTask);
        return;
      }

      setTasks(tasks.map(task =>
        task.id === taskId
          ? { ...task, is_visible: !currentVisibility }
          : task
      ));
    } catch (error) {
      console.error('Error updating task visibility:', error);
      alert(t.errorUpdatingTask);
    }
  }; */

  const SortableTaskItem = ({ task, index }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: task.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
      backgroundColor: task.completed ? '#f0f9f0' : 'transparent', // Verde claro si completada
      borderRadius: 1,
      mb: 2,
      border: isDragging ? `2px dashed ${office?.color}` : 'none',
    };

    const docs = taskDocuments[task.id] || [];
    const isExpanded = expandedTasks[task.id] || false;
    const docCount = docs.length;

    return (
      <Box ref={setNodeRef} style={style} sx={{ position: 'relative' }}>
        <ListItem 
          data-task-id={task.id}
          sx={{ 
            px: 0, 
            py: 1,
            backgroundColor: docCount > 0 ? (task.completed ? '#e8f5e9' : '#f8f9fa') : 'transparent',
            borderRadius: 1,
            border: docCount > 0 ? '1px solid #e9ecef' : 'none',
          }}
        >
          {/* DRAG HANDLE - Solo visible si no está completada y no es readonly */}
          {!task.completed && !isCaseReadOnly() && caseRole !== 'client' && (
            <ListItemIcon 
              {...attributes} 
              {...listeners}
              sx={{ 
                minWidth: 40, 
                cursor: 'grab',
                '&:active': { cursor: 'grabbing' },
                color: 'text.secondary'
              }}
            >
              <DragIndicator />
            </ListItemIcon>
          )}

          <ListItemIcon>
            <Box sx={{
              position: 'relative',
              '& .MuiCheckbox-root': {
                transform: animatingTask === task.id ? 'scale(1.3)' : 'scale(1)',
                transition: animatingTask === task.id 
                  ? 'all 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275)' 
                  : 'transform 0.2s ease',
                '&.Mui-checked': {
                  '& .MuiSvgIcon-root': {
                    animation: animatingTask === task.id ? 'checkInflate 0.8s ease-in-out' : 'none',
                  }
                }
              },
              // Animación mejorada del checkbox - efecto de inflado
              '@keyframes checkInflate': {
                '0%': { 
                  transform: 'scale(0.8)',
                  opacity: 0.7
                },
                '30%': { 
                  transform: 'scale(1.4)',
                  opacity: 1
                },
                '60%': { 
                  transform: 'scale(0.9)',
                  opacity: 1
                },
                '80%': { 
                  transform: 'scale(1.1)',
                  opacity: 1
                },
                '100%': { 
                  transform: 'scale(1)',
                  opacity: 1
                }
              },
              // Efecto de "glow" alrededor del checkbox mejorado
              '&::after': animatingTask === task.id ? {
                content: '""',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                background: `radial-gradient(circle, ${office?.color}30 0%, ${office?.color}10 50%, transparent 70%)`,
                animation: 'glowPulse 1s ease-out',
                pointerEvents: 'none'
              } : {},
              '@keyframes glowPulse': {
                '0%': {
                  transform: 'translate(-50%, -50%) scale(0.5)',
                  opacity: 0
                },
                '50%': {
                  transform: 'translate(-50%, -50%) scale(1.2)',
                  opacity: 0.8
                },
                '100%': {
                  transform: 'translate(-50%, -50%) scale(2)',
                  opacity: 0
                }
              }
            }}>
              <Checkbox
                checked={task.completed}
                onChange={() => {
                  if (!isCaseReadOnly() && caseRole !== 'client') {
                    handleToggleTask(task.id);
                  }
                }}
                disabled={isCaseReadOnly() || caseRole === 'client'}
                sx={{
                  color: task.completed ? '#4caf50' : office?.color,
                  '&.Mui-checked': { color: '#4caf50' }
                }}
              />
            </Box>
          </ListItemIcon>

          <ListItemText
            primary={
              editingTask?.id === task.id ? (
                <TaskEditForm
                  key={`edit-form-${task.id}`}
                  task={task}
                  office={office}
                  onSave={handleSaveEditFromForm}
                  onCancel={handleCancelEdit}
                  t={t}
                  isValidDate={isValidDate}
                />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      textDecoration: task.completed ? 'line-through' : 'none',
                      color: task.completed ? '#4caf50' : 'text.primary',
                      fontWeight: task.completed ? 'normal' : 'medium',
                      opacity: task.completed ? 0.8 : 1
                    }}
                  >
                    {task.text}
                  </Typography>
                  {task.completed && (
                    <Chip 
                      label="Completada" 
                      size="small" 
                      sx={{ 
                        backgroundColor: '#4caf50', 
                        color: 'white',
                        fontSize: '0.7rem'
                      }} 
                    />
                  )}
                  {!task.is_visible && caseRole !== 'client' && (
                    <Chip 
                      label="Oculta para cliente" 
                      size="small" 
                      sx={{ 
                        backgroundColor: '#ff9800', 
                        color: 'white',
                        fontSize: '0.7rem'
                      }} 
                    />
                  )}
                  {task.notes  && (
                    <Tooltip title={task.notes}>
                      <Chip 
                        icon={<Info sx={{ fontSize: '0.9rem', color: 'white !important' }} />}
                        label="Tiene notas" 
                        size="small" 
                        onClick={() => setViewingNotesTaskId(viewingNotesTaskId === task.id ? null : task.id)}
                        sx={{ 
                          backgroundColor: office?.color, 
                          color: 'white',
                          fontSize: '0.7rem',
                          cursor: 'pointer',
                          '&:hover': { opacity: 0.8 }
                        }} 
                      />
                    </Tooltip>
                  )}
                  {docCount > 0 && (
                    <Chip 
                      label={`${docCount} ${docCount === 1 ? 'archivo' : 'archivos'}`}
                      size="small"
                      sx={{
                        backgroundColor: office?.color,
                        color: 'white',
                        fontSize: '0.7rem'
                      }}
                    />
                  )}
                </Box>
              )
            }
            secondary={
              task.due_date && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Vence: {formatDate(task.due_date, caseData?.language)}
                  </Typography>
                  {!task.completed && new Date(task.due_date) < new Date() && (
                    <Chip 
                      label="Vencida" 
                      size="small" 
                      sx={{ 
                        backgroundColor: '#d32f2f', 
                        color: 'white',
                        fontSize: '0.65rem',
                        height: '18px'
                      }} 
                    />
                  )}
                </Box>
              )
            }
          />

          {!editingTask && <Box sx={{ display: 'flex', gap: 1 }}>
            {/* Botones existentes... */}
            {docCount > 0 && (
              <IconButton
                size="small"
                onClick={() => toggleTaskExpand(task.id)}
                sx={{ color: office?.color }}
              >
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            )}

            {!task.completed && !isCaseReadOnly() && (
              <Tooltip title={t.uploadDocument || 'Subir documento'}>
                <IconButton
                  size="small"
                  onClick={() => handleOpenUploadModal(task)}
                  disabled={uploadingForTask === task.id}
                  sx={{ color: office?.color }}
                >
                  {uploadingForTask === task.id ? (
                    <CircularProgress size={20} />
                  ) : (
                    <UploadFile />
                  )}
                </IconButton>
              </Tooltip>
            )}

            {/* {caseRole !== 'client' && !isCaseReadOnly() && (
              <Tooltip title={task.is_visible ? (t.hideFromClient || 'Ocultar del cliente') : (t.showToClient || 'Mostrar al cliente')}>
                <IconButton
                  size="small"
                  onClick={() => handleToggleTaskVisibility(task.id, task.is_visible)}
                  sx={{ 
                    color: task.is_visible ? office?.color : '#ff9800',
                    backgroundColor: task.is_visible ? 'transparent' : '#fff3e0'
                  }}
                >
                  {task.is_visible ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </Tooltip>
            )} */}

            {caseRole !== 'client' && !task.completed && !isCaseReadOnly() && (
              <IconButton
                size="small"
                onClick={() => handleEditTask(task.id)}
                sx={{ color: office?.color }}
              >
                <EditTaskIcon />
              </IconButton>
            )}

            {caseRole !== 'client' && !isCaseReadOnly() && (
              <IconButton
                size="small"
                onClick={() => handleDeleteTask(task.id)}
                sx={{ color: '#d32f2f' }}
              >
                <DeleteTaskIcon />
              </IconButton>
            )}
          </Box>}
        </ListItem>

        {docCount > 0 && (
        <Collapse in={isExpanded}>
          <Box sx={{ ml: 7, mt: 1, mb: 2 }}>
            <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#fafafa' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                Documentos adjuntos:
              </Typography>
              <List dense>
                {docs.map((doc) => (
                  <ListItem
                    key={doc.id}
                    sx={{
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      mb: 1,
                      backgroundColor: 'white'
                    }}
                    secondaryAction={
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {/* Botón para abrir en Google Drive (visualización) */}
                        {(doc.drive_url || doc.google_drive_id) && (
                          <Tooltip title={t.openInDrive || 'Abrir en Google Drive'}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenInDrive(doc, false)}
                              sx={{ color: office?.color }}
                            >
                              <OpenInNew fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {/* Botón para editar en Google Docs (solo Word/Google Docs) */}
                        {(doc.drive_url || doc.google_drive_id) && (isWordDocument(doc) || isGoogleDocument(doc)) && (
                          <Tooltip title={t.editInGoogleDocs || 'Editar en Google Docs'}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenInDrive(doc, true)}
                              sx={{ color: office?.color }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {/* Botón de descarga */}
                        <Tooltip title={t.downloadDocument || 'Descargar'}>
                          <IconButton
                            size="small"
                            onClick={() => handleDownloadDocument(doc)}
                            disabled={downloading[doc.id]}
                            sx={{ color: office?.color }}
                          >
                            {downloading[doc.id] ? (
                              <CircularProgress size={16} />
                            ) : (
                              <Download fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        
                        {caseRole !== 'client' && !isCaseReadOnly() && (
                          <Tooltip title={t.deleteDocument || 'Eliminar'}>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteDocument(doc.id, task.id)}
                              sx={{ color: '#d32f2f' }}
                            >
                              <DeleteTaskIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    }
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Assignment fontSize="small" sx={{ color: office?.color }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {/* Chip de Google Drive */}
                          {(doc.drive_url || doc.google_drive_id) && (
                            <Chip 
                              label={isWordDocument(doc) ? "Editable en Google Docs" : "Ver en Drive"} 
                              size="small" 
                              variant="outlined"
                              sx={{ 
                                fontSize: '0.65rem',
                                height: '18px',
                                borderColor: '#4285f4',
                                color: '#4285f4'
                              }} 
                            />
                          )}
                          
                          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                            {doc.name || doc.filename}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                          {doc.size_bytes && (
                            <Typography variant="caption" color="text.secondary">
                              {formatFileSize(parseInt(doc.size_bytes))}
                            </Typography>
                          )}
                          {doc.extension && (
                            <Typography variant="caption" color="text.secondary">
                              {doc.extension.toUpperCase()}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(doc.created_at, caseData?.language)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Box>
        </Collapse>
      )}

      {task.notes && (
        <Collapse in={viewingNotesTaskId === task.id}>
          <Box sx={{ ml: 7, mt: 1, mb: 2 }}>
            <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#fffef7', borderLeft: `3px solid ${office?.color}` }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: office?.color, fontWeight: 'bold' }}>
                📝 {t.internalNotes || 'Notas internas'}:
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}>
                {task.notes}
              </Typography>
            </Paper>
          </Box>
        </Collapse>
      )}
      </Box>
    );
  };

  if (isLoadingPermissions) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Skeleton variant="text" width={200} height={40} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="100%" height={20} sx={{ mb: 3 }} />
        {[1, 2, 3].map((i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
            <Skeleton variant="rectangular" width="80%" height={50} />
          </Box>
        ))}
      </Paper>
    );
  }

  return (
    <>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            {t.caseTasks}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Botón de descargar todos - visible si hay documentos */}
          {Object.values(taskDocuments).flat().length > 0 && (
            <Button
              variant="outlined"
              startIcon={downloadingAll ? <CircularProgress size={20} /> : <Download />}
              onClick={handleDownloadAllDocuments}
              disabled={downloadingAll}
              sx={{ 
                borderColor: office?.color, 
                color: office?.color 
              }}
            >
              {downloadingAll 
                ? (t.downloading || 'Descargando...') 
                : (t.downloadAll || 'Descargar Todos')
              }
            </Button>
          )}
          </Box>

          {caseRole !== 'client' && !isCaseReadOnly() && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setIsAddingTask(true)}
              sx={{ borderColor: office?.color, color: office?.color }}
            >
              {t.addNewTask}
            </Button>
          )}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {isCaseReadOnly() ? t.viewTasksProgress : t.manageTasksProgress}
        </Typography>

        {/* Nueva tarea */}
        {isAddingTask && !isCaseReadOnly() && (
          <Paper 
            elevation={3} 
            sx={{ 
              mb: 3, 
              p: 3, 
              backgroundColor: '#fafafa',
              border: `2px solid ${office?.color}`,
              borderRadius: 2
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: office?.color }}>
              ➕ {t.addNewTask || 'Agregar Nueva Tarea'}
            </Typography>
            
            <Grid container spacing={2}>
              {/* Descripción de la tarea */}
              <Grid item size={{xs: 12}}>
                <TextField
                  fullWidth
                  label={t.taskDescription || 'Descripción de la tarea'}
                  placeholder={t.taskDescriptionPlaceholder || 'Ej: Preparar documentación para el cliente'}
                  value={newTask}
                  onChange={(e) => setNewTask(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.ctrlKey) {
                      handleAddTask();
                    } else if (e.key === 'Escape') {
                      setIsAddingTask(false);
                      setNewTask('');
                      setNewTaskDueDate('');
                    }
                  }}
                  variant="outlined"
                  multiline
                  rows={2}
                  autoFocus
                  helperText={t.pressCtrlEnter || 'Ctrl + Enter para guardar, Esc para cancelar'}
                />
              </Grid>

              {/* Fecha de vencimiento */}
              <Grid item size={{xs: 12, sm:6}}>
                  <TextField
                    fullWidth
                    type="date"
                    label={t.dueDate || 'Fecha de vencimiento'}
                    value={newTaskDueDate}
                    onChange={(e) => setNewTaskDueDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    error={newTaskDueDate && !isValidDate(newTaskDueDate)}
                    helperText={
                      newTaskDueDate && !isValidDate(newTaskDueDate)
                        ? (t.invalidDateFormat || 'Formato inválido')
                        : (t.optional || 'Opcional')
                    }
                  />
                </Grid>

                {/* Visibilidad */}
                <Grid item size={{xs: 12, sm:6}}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    height: '100%',
                    p: 2,
                    backgroundColor: 'white',
                    borderRadius: 1,
                    border: '1px solid #e0e0e0'
                  }}>
                    <Checkbox
                      checked={newTaskVisible}
                      onChange={(e) => setNewTaskVisible(e.target.checked)}
                      sx={{
                        color: office?.color,
                        '&.Mui-checked': { color: office?.color }
                      }}
                    />
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {t.visibleToClient || 'Visible para el cliente'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t.clientCanSeeTask || 'El cliente podrá ver esta tarea'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Botones de acción */}
                <Grid item size={{xs: 12}}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setIsAddingTask(false);
                        setNewTask('');
                        setNewTaskDueDate('');
                        setNewTaskVisible(true);
                      }}
                      sx={{ minWidth: 100 }}
                    >
                      {t.cancel || 'Cancelar'}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleAddTask}
                      disabled={!newTask.trim()}
                      startIcon={<AddIcon />}
                      sx={{ 
                        backgroundColor: office?.color,
                        minWidth: 150,
                        '&:hover': { backgroundColor: office?.color, opacity: 0.9 }
                      }}
                    >
                      {t.addTask || 'Agregar Tarea'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          )}

        {/* NUEVO RENDERIZADO CON DRAG & DROP */}
        {sortedTasks.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ color: office?.color, fontWeight: 'bold', mb: 1 }}>
              Tareas ({caseRole === 'client' ? visibleTasks.length : sortedTasks.length})
            </Typography>
            
            <DndContext 
              sensors={sensors} 
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext 
                items={sortedTasks.map(t => t.id)} 
                strategy={verticalListSortingStrategy}
              >
                <List>
                  {visibleTasks.map((task, index) => (
                    <SortableTaskItem key={task.id} task={task} index={index} />
                  ))}
                </List>
              </SortableContext>
            </DndContext>
          </>
        )}

        {/* Mensaje cuando no hay tareas */}
        {sortedTasks.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              {t.noTasksCreated}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Modal de subida de documentos */}
      <DocumentUploadModal
        open={openUploadModal}  // Eliminar la condición && !storageTypeLoading
        onClose={() => {
          setOpenUploadModal(false);
          setSelectedTaskForUpload(null);
        }}
        onUpload={handleUploadDocument}
        officeColor={office?.color}
        language={caseData?.language}
        readOnly={isCaseReadOnly()}
      />

      <DocumentMetadataModal
        open={!!metadataDoc}
        documentName={metadataDoc?.name}
        saving={savingMetadata}
        onSave={handleMetadataSave}
        onSkip={handleMetadataSkip}
      />

      {/* Modal de Confirmación para Completar Tarea */}
      <Dialog
        open={openTaskConfirmModal}
        onClose={() => setOpenTaskConfirmModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon sx={{ color: '#ff9800' }} />
          {t.confirmCompleteTask}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t.aboutToCompleteTask}
          </DialogContentText>
          <Box sx={{ 
            p: 2, 
            backgroundColor: '#f5f5f5', 
            borderRadius: 1, 
            mb: 2,
            border: `1px solid ${office?.color}20`
          }}>
            <Typography variant="body1" fontWeight="bold">
              "{taskToComplete?.text}"
            </Typography>
          </Box>
          <DialogContentText sx={{ mb: 2 }}>
            {t.whenCompletingTask}
          </DialogContentText>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
              • {t.taskCannotBeEdited}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
              • {t.taskCannotBeUnchecked}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
              • {t.taskCanOnlyBeDeleted}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
              • {t.progressWillBeUpdated}
            </Typography>
          </Box>
          <DialogContentText>
            {t.confirmCompleteTaskQuestion}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setOpenTaskConfirmModal(false);
              setTaskToComplete(null);
            }}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            {t.cancel}
          </Button>
          <Button
            onClick={handleConfirmCompleteTask}
            variant="contained"
            sx={{ 
              backgroundColor: office?.color,
              '&:hover': { backgroundColor: office?.color, opacity: 0.9 }
            }}
          >
            {t.completeTask}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TasksSection;